/**
 * A single categorized group in the global-search dropdown. Renders the
 * bucket header, its hit rows, and a "See all" footer row when the server
 * says there are more matches than we're showing. Split out of
 * `global-search.tsx` so the main component stays under the 300-line
 * threshold.
 */

import { Command } from 'cmdk';
import { ArrowRight } from 'lucide-react';
import { Bucket } from './global-search-hits';

interface Props {
  bucket: Bucket;
  inactiveText: string;
  normalText: string;
  onOpen: (href: string) => void;
  onSeeAll: () => void;
  t: (k: string) => string;
}

export default function BucketGroup({
  bucket,
  inactiveText,
  normalText,
  onOpen,
  onSeeAll,
  t
}: Props) {
  return (
    <Command.Group
      heading={
        <div
          className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide flex items-center justify-between border-b border-hairline"
          style={{ color: inactiveText }}
        >
          <span>{bucket.title}</span>
          <span>{bucket.total}</span>
        </div>
      }
    >
      {bucket.hits.map(hit => (
        <Command.Item
          key={hit.key}
          value={hit.key}
          onSelect={() => onOpen(hit.href)}
          className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer aria-selected:bg-app hover:bg-app"
        >
          <hit.icon
            className="h-4 w-4 shrink-0"
            style={{ color: inactiveText }}
          />
          <span
            className="flex-1 truncate text-sm"
            style={{ color: normalText }}
          >
            {hit.label}
          </span>
          {hit.meta && (
            <span className="text-xs shrink-0" style={{ color: inactiveText }}>
              {hit.meta}
            </span>
          )}
        </Command.Item>
      ))}
      {bucket.hasMore && (
        <Command.Item
          value={`seeall:${bucket.key}`}
          onSelect={onSeeAll}
          className="w-full text-left flex items-center gap-1 px-3 py-1.5 rounded-md cursor-pointer text-xs aria-selected:bg-app hover:bg-app"
          style={{ color: normalText }}
        >
          <span>
            {t('see-all') || 'See all'} ({bucket.total})
          </span>
          <ArrowRight className="h-3 w-3" />
        </Command.Item>
      )}
    </Command.Group>
  );
}
